<template>
    <loader :loader="isLoading" v-if="isLoading" />
    <div class="animated fadeIn" v-else>
        <b-card>
            <b-container fluid>
                <b-row no-gutters>
                    <b-col>
                        <kpi-header
                            :target="target"
                            :current-user-id="currentUser.id"
                            :current-event-permissions="currentUser.permissions.goals"
                            :isAllowedToEdit="isUserAllowedToEdit"
                            @updated="getTargetFromAPI()"
                            :indicatorType="'kpi'"
                        />
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <b-card>
            <b-container fluid>
                <b-row>
                    <b-col md="4" xl="3">
                        <c-informations class="mb-3" :data="informations" />
                        <c-ranges :ranges="target.ranges" :indicatorType="'kpi'" />
                    </b-col>
                    <b-col md="8" xl="9">
                        <b-tabs>
                            <b-tab :title="$t('targets.details')">
                                <c-details
                                    :isAllowedToEdit="isUserAllowedToEdit"
                                    @update-parent="updateParent"
                                    :indicator="target"
                                />
                            </b-tab>
                            <b-tab :title="$t('targets.description')">
                                <template v-if="target.description">
                                    <p v-html="target.description"></p>
                                </template>
                                <b-alert show variant="info" v-else>
                                    {{ $t('targets.informations.noDescriptionToDisplay') }}
                                </b-alert>
                            </b-tab>
                            <b-tab :title="$t('targets.settings')" v-if="isUserAllowedToEdit">
                                <c-settings
                                    :target="target"
                                    @update-parent="updateParent"
                                    :current-event-permissions="currentUser.permissions.goals"
                                />
                            </b-tab>
                        </b-tabs>
                    </b-col>
                </b-row>
            </b-container>
        </b-card>
        <router-view :data="getModalData" @update-parent="updateParent"></router-view>
    </div>
</template>

<script>
import api from '@/services/api'
import { getParam as getUrlParam } from '@/lib/url'
import Loader from '@/views/components/loader'
import i18n from '@/i18n'
import KpiHeader from './components/kpi/kpi-header'
import CRanges from '@/views/target/components/view/side-menu/ranges'
import CInformations from '@/views/target/components/view/side-menu/informations'
import CDetails from '@/views/target/components/view/tab-content/details'
import CSettings from './components/kpi/tab-content'

export default {
    name: 'Kpi',
    components: {
        Loader,
        CRanges,
        CInformations,
        CDetails,
        CSettings,
        KpiHeader,
    },
    data() {
        return {
            id: getUrlParam('kpiId', this),
            isLoading: true,
            informations: {},
            target: {},
            tags: [],
            unreadCommentsCount: null,
            unreadCommentsIds: null,
        }
    },
    mounted() {
        this.getTargetFromAPI()
    },
    methods: {
        getTargetFromAPI() {
            this.isLoading = true
            api.get(`/kpi/${this.id}/edit`)
                .then((resp) => {
                    const {
                        data: { data },
                    } = resp

                    const {
                        achieved,
                        colors,
                        departments,
                        description,
                        formula,
                        formula_html,
                        has_check_in_available,
                        has_trigger_goal,
                        id,
                        indicatorUsersIds,
                        initial_value,
                        is_calculated,
                        kpi_checkins,
                        lastCheckInValue,
                        level,
                        occupation_groups,
                        only_focal_point,
                        owner,
                        period,
                        polarity,
                        progress,
                        range,
                        ranges,
                        section,
                        status,
                        tags,
                        target,
                        title,
                        type,
                        unit_of_measurement,
                        useProgressOnAutomaticCheckin,
                        user_relations,
                        visualization_type,
                        year_to_date,
                    } = data

                    this.informations = {
                        id: owner.id,
                        owner: owner.name,
                        period: period.name,
                        managerId: owner.manager_id,
                        level: this.getLevel(level, section),
                        yearToDate: i18n.t(`yearToDate.${year_to_date.title}`),
                        hasTriggerGoal: this.getHasTriggerGoal(),
                        UnitOfMeasurement: unit_of_measurement.title,
                    }

                    this.target = {
                        achieved,
                        colors,
                        departments,
                        description,
                        formula,
                        formulaHtml: formula_html,
                        hasCheckInAvailable: has_check_in_available,
                        hasTriggerGoal: has_trigger_goal,
                        id,
                        indicatorUsersIds,
                        initialValue: initial_value,
                        isCalculated: is_calculated,
                        kpiCheckins: kpi_checkins,
                        lastCheckInValue,
                        level,
                        occupationGroups: occupation_groups,
                        onlyFocalPoint: only_focal_point,
                        owner,
                        period,
                        polarity,
                        progress,
                        range,
                        ranges,
                        section,
                        status,
                        target,
                        title,
                        type,
                        useProgressOnAutomaticCheckin,
                        userRelations: user_relations,
                        visualizationType: visualization_type,
                        yearToDate: year_to_date,
                    }
                    this.tags = tags
                })
                .catch((e) => {
                    console.log(e)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        getLevel(level, section) {
            return level == 'personal' ? i18n.t('groupLevel.personal') : section
        },
        getHasTriggerGoal() {
            return null
        },
        updateParent(action, data) {
            switch (action) {
                case 'updateTags':
                    this.tags = data ? data : []
                    break
                case 'updateCheckin':
                    this.getTargetFromAPI()
                    break
                case 'updateOnlyFocalPoint':
                    this.target.onlyFocalPoint = data
                    break
                case 'updateHasTriggerGoal':
                    this.target.hasTriggerGoal = data
                    break
                case 'updateSuggest':
                    this.target.departments = data.departments
                    this.target.occupationGroups = data.occupation_groups
                    this.target.userRelations = data.user_relations
                    break
                case 'updateCalculation':
                    this.target.isCalculated = data.is_calculated
                    this.target.useProgressOnAutomaticCheckin = data.useProgressOnAutomaticCheckin
                    this.target.formula = data.formula
                    this.target.formulaHtml = data.formula_html
                    this.target.kpiCheckins = data.kpi_checkins
                    this.target.lastCheckInValue = data.lastCheckInValue
                    this.target.progress = data.progress
                    this.target.range = data.range
                    this.target.achieved = data.achieved
                    break
                default:
                    break
            }
        },
        openModalCheckInEdit(data) {
            const { targetId, checkinId } = data
            this.$router.push({
                path: `/v2/target/targets/view/${targetId}/checkin/${checkinId}`,
            })
        },
    },
    computed: {
        getModalData() {
            return {
                tags: this.tags,
                departments: this.target.departments,
                occupationGroups: this.target.occupationGroups,
                userRelations: this.target.userRelations,
                targetLevelPermissions: this.currentUser.permissions.goals[this.target.level],
                isCalculated: this.target.isCalculated,
                useProgressOnAutomaticCheckin: this.target.useProgressOnAutomaticCheckin,
                formula: this.target.formula,
                formulaHtml: this.target.formulaHtml,
                module: 'kpi',
                indicatorId: this.target.id,
            }
        },
        currentUser() {
            return this.$store.state.currentUser.user
        },
        currentUserIsAdminOrGod() {
            return this.currentUser.role.name == 'administrator' || this.currentUser.role.name == 'god'
        },
        isUserAllowedToEdit() {
            return this.currentUser.id == this.informations.id || this.currentUserIsAdminOrGod
        },
    },
}
</script>

<style>
button:disabled {
    cursor: not-allowed !important;
    pointer-events: all !important;
}
</style>
